// src/service/SubscriptionWellDhanService.jsx

import axios from 'axios';
import { get, post } from './ApiService';
import {
  axiosSubscriptionInstance,
  axiosUserProfileInstance,
} from '../configs/AxiosInstance';

/** fetch-all-users-optimized is served by microservice-user-profile via Kong .../auth/welldhan (see axiosUserProfileInstance). */
const userListApiInstance = axiosUserProfileInstance;

// 1. Get Common Utils
export const getSubscriptionCommonUtils = async () => {
  try {
    const data = await get('/get-common-utils', { instance: axiosSubscriptionInstance });
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to fetch subscription utils. Please try again.",
    };
  }
};

// 2. Fetch All User Details (with pagination, search, sorting, and filtering)
// Uses optimized endpoint from microservice-user-profile for better performance (sub-100ms)
export const fetchAllUserDetails = async (
  event_type_id,
  limit = 100,
  offset = 0,
  search = null,
  sort_by = null,
  sort_order = "asc",
  filters = null,
  my_users_only = false,
  { signal } = {}
) => {
  try {
    // Use optimized endpoint from user-profile microservice for better performance
    let query = `/fetch-all-users-optimized?application_name=flexcellence_app&limit=${limit}&offset=${offset}`;

    if (search) {
      query += `&search=${encodeURIComponent(search)}`;
    }
    if (sort_by) {
      query += `&sort_by=${encodeURIComponent(sort_by)}`;
    }
    if (sort_order) {
      query += `&sort_order=${encodeURIComponent(sort_order)}`;
    }
    if (filters) {
      query += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
    }
    if (my_users_only) {
      query += `&my_users_only=true`;
    }

    const data = await get(query, { instance: userListApiInstance, signal });
    
    return { success: true, data };
  } catch (error) {
    // Superseded by a newer query — not a failure worth surfacing.
    if (axios.isCancel(error)) {
      return { success: false, aborted: true };
    }
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to fetch all user details. Please try again.",
    };
  }
};

// 2b. Per-client coaching signals for the chat roster tags.
// Aggregates what would otherwise be one call per client across four services
// (diet plans, workout plans, food logs, workout logs, subscription coaches).
/**
 * @param {string} event_type_id
 * @param {Object} [options]
 * @param {number} [options.tracking_window_days=7] - Window the log counts cover.
 * @param {string[]} [options.client_ids] - Scope to specific clients; omit for the whole roster.
 * @param {number} [options.limit=500] - Roster-sized, not page-sized.
 * @param {number} [options.offset=0]
 * @returns {Promise<{success: boolean, data?: Object, notImplemented?: boolean, message?: string}>}
 */
export const fetchCoachClientInsights = async (event_type_id, options = {}) => {
  if (!event_type_id) {
    return { success: false, message: "Event type ID is required." };
  }

  // The endpoint derives the coach from the JWT and has no `coach_id` param —
  // a coach must not be able to read another coach's roster by editing a URL.
  // FastAPI drops unknown query params silently, so passing one used to return
  // the CALLER's own roster while looking entirely correct. Fail loudly instead;
  // a manager-facing per-coach roster needs the backend change first.
  if (options.coach_id) {
    return {
      success: false,
      message:
        "fetch-coach-client-insights cannot be scoped to another coach yet — it always returns the authenticated coach's roster.",
    };
  }

  const {
    tracking_window_days = 7,
    client_ids = null,
    limit = 500,
    offset = 0,
  } = options;

  try {
    let query =
      `/fetch-coach-client-insights?application_name=flexcellence_app` +
      `&event_type_id=${encodeURIComponent(event_type_id)}` +
      `&tracking_window_days=${tracking_window_days}` +
      `&limit=${limit}&offset=${offset}`;

    if (Array.isArray(client_ids) && client_ids.length) {
      query += `&client_ids=${encodeURIComponent(client_ids.join(","))}`;
    }

    const data = await get(query, { instance: userListApiInstance });

    return { success: true, data };
  } catch (error) {
    const httpStatus = error?.response?.status;
    // 404/501 = the endpoint is not deployed yet; 403 = this coach is not
    // entitled. All three are permanent for the session, so the caller latches
    // on this flag and stops asking rather than retrying every TTL window.
    const notImplemented =
      httpStatus === 404 || httpStatus === 501 || httpStatus === 403;

    return {
      success: false,
      notImplemented,
      message:
        error?.response?.data?.detail ||
        "Failed to fetch coach client insights. Please try again.",
    };
  }
};

// 4b. Fetch employees (for sales rep dropdown) – same backend as corporate
export const fetchEmployeesForSubscription = async () => {
  try {
    const data = await get("/fetch-employees", { instance: axiosSubscriptionInstance });
    const employees = data?.data?.employees ?? data?.employees ?? [];
    return { success: true, data: Array.isArray(employees) ? employees : [] };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to fetch employees.",
      data: [],
    };
  }
};

export const fetchClientSubscriptionsByClient = async (client_id) => {
  if (!client_id) {
    return { success: false, message: "Client ID is required." };
  }
  try {
    const id = typeof client_id === "string" ? client_id : String(client_id);
    const query = `/get-user-subscription-details?client_id=${encodeURIComponent(id)}`;
    const data = await get(query, { instance: axiosSubscriptionInstance });
    return { success: true, data };
  } catch (error) {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    let message = "Failed to fetch client subscriptions. Please try again.";
    if (status === 404) {
      message = "Subscriptions endpoint not found. You can still add a new subscription.";
    } else if (typeof detail === "string") {
      message = detail;
    }
    return { success: false, message };
  }
};

// 6. Modify Client Subscriptions (ADD/UPDATE/DELETE batch)
export const modifyClientSubscriptions = async (payload) => {
  try {
    const { data } = await post('/modify-client-subscriptions', payload, { instance: axiosSubscriptionInstance });
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to modify client subscriptions. Please try again.",
    };
  }
};

// 7. Modify Client Subscription Coaches (ADD/UPDATE/DELETE batch)
export const modifyClientSubscriptionCoaches = async (payload) => {
  try {
    const { data } = await post('/modify-client-subscription-coaches', payload, { instance: axiosSubscriptionInstance });
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to modify client subscription coaches. Please try again.",
    };
  }
};

// 7b. Executive analytics API (Executive department only)
export const fetchExecutiveAnalytics = async (groupBy = "monthly", limit = 36, start = null, end = null) => {
  try {
    let query = `/executive-analytics?group_by=${encodeURIComponent(groupBy)}&limit=${limit}`;
    if (start) query += `&start=${encodeURIComponent(start)}`;
    if (end) query += `&end=${encodeURIComponent(end)}`;
    const data = await get(query, { instance: axiosSubscriptionInstance });
    return { success: true, data: data?.data ?? data };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to fetch executive analytics.",
      data: { rows: [], group_by: groupBy },
    };
  }
};

// 7c. User Overview Analytics (for Executive analytics dashboard) with 1-hour in-memory cache
const _userOverviewCache = new Map(); // key -> { data, ts }
const USER_OVERVIEW_TTL_MS = 60 * 60 * 1000; // 1 hour

export const fetchUserOverviewAnalytics = async (filters = {}) => {
  // Build a stable cache key from the filters
  const cacheKey = JSON.stringify(filters);
  const cached = _userOverviewCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < USER_OVERVIEW_TTL_MS) {
    return { success: true, data: cached.data, fromCache: true };
  }

  try {
    let query = `/analytics/user-overview?application_name=flexcellence_app`;
    if (filters.gender) query += `&gender=${encodeURIComponent(filters.gender)}`;
    if (filters.age_min != null) query += `&age_min=${filters.age_min}`;
    if (filters.age_max != null) query += `&age_max=${filters.age_max}`;
    if (Array.isArray(filters.locations) && filters.locations.length) {
      filters.locations.forEach(l => { query += `&locations[]=${encodeURIComponent(l)}`; });
    }
    if (Array.isArray(filters.activity_levels) && filters.activity_levels.length) {
      filters.activity_levels.forEach(a => { query += `&activity_levels[]=${encodeURIComponent(a)}`; });
    }
    if (Array.isArray(filters.dietary_preferences) && filters.dietary_preferences.length) {
      filters.dietary_preferences.forEach(d => { query += `&dietary_preferences[]=${encodeURIComponent(d)}`; });
    }
    if (Array.isArray(filters.fitness_goals) && filters.fitness_goals.length) {
      filters.fitness_goals.forEach(g => { query += `&fitness_goals[]=${encodeURIComponent(g)}`; });
    }

    const data = await get(query, { instance: axiosUserProfileInstance });
    const result = data?.data ?? data;
    _userOverviewCache.set(cacheKey, { data: result, ts: Date.now() });
    return { success: true, data: result };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to fetch user overview analytics.",
      data: null,
    };
  }
};

// 8. Modify Client Subscription Extensions (ADD/UPDATE/DELETE batch)
export const modifyClientSubscriptionExtensions = async (payload) => {
  try {
    const { data } = await post('/modify-client-subscription-extensions', payload, { instance: axiosSubscriptionInstance });
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to modify client subscription extensions. Please try again.",
    };
  }
};
